<template>
  <div class="role-selection mt-5">
    <div class="text-center mb-5">
      <p class="eyebrow">{{ $t("signup.role_selection.eyebrow") }}</p>
      <h1 class="page-title font-serif mb-3">
        {{ $t("signup.role_selection.title") }}
      </h1>
      <p class="lead text-body-secondary mx-auto subtitle">
        {{ $t("signup.role_selection.subtitle") }}
      </p>
    </div>

    <div class="row g-4 mb-4">
      <div v-for="path in PATHS" :key="path.role" class="col-md-6">
        <div
          class="path-card h-100"
          role="button"
          tabindex="0"
          :data-testid="`role-${path.role}`"
          @click="selectRole(path.role)"
          @keydown.enter="selectRole(path.role)"
          @keydown.space.prevent="selectRole(path.role)"
        >
          <div class="d-flex align-items-center gap-3 mb-3">
            <span class="path-icon">
              <FontAwesomeIcon :icon="path.icon" fixed-width />
            </span>
            <p class="path-eyebrow mb-0">
              {{ $t(`signup.role_selection.${path.role}_title`) }}
            </p>
          </div>
          <h2 class="path-title font-serif mb-2">
            {{ $t(`signup.role_selection.${path.role}_headline`) }}
          </h2>
          <p class="text-body-secondary mb-3">
            {{ $t(`signup.role_selection.${path.role}_lead`) }}
          </p>
          <ul class="list-unstyled mb-4">
            <li
              v-for="benefit in BENEFITS"
              :key="benefit"
              class="path-benefit d-flex align-items-start gap-2"
            >
              <FontAwesomeIcon icon="check" class="benefit-icon mt-1" />
              <span>
                {{ $t(`signup.role_selection.${path.role}_benefit${benefit}`) }}
              </span>
            </li>
          </ul>
          <span class="btn btn-primary path-button w-100 mt-auto">
            {{ $t(`signup.role_selection.${path.role}_cta`) }}
            <FontAwesomeIcon icon="arrow-right" class="ms-2" />
          </span>
        </div>
      </div>
    </div>

    <div
      class="help-card d-flex flex-column flex-md-row align-items-md-center gap-3 mb-5"
    >
      <div class="flex-grow-1">
        <h3 class="help-title font-serif mb-1">
          {{ $t("signup.role_selection.help_title") }}
        </h3>
        <p class="text-body-secondary mb-0">
          {{ $t("signup.role_selection.help_description") }}
        </p>
      </div>
      <a
        :href="DEMO_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="btn btn-outline-primary flex-shrink-0"
        data-testid="role-help-demo"
      >
        <FontAwesomeIcon icon="calendar-check" class="me-2" />
        {{ $t("signup.role_selection.help_button") }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";

type Role = "corporate" | "producer";

// A mixed case (a corporation with some production) is a conversation, so it goes to a demo.
const DEMO_URL = "https://www.soldera.org/demo";

const PATHS: { role: Role; icon: string }[] = [
  { role: "corporate", icon: "building" },
  { role: "producer", icon: "solar-panel" },
];

const BENEFITS = [1, 2, 3, 4] as const;

const router = useRouter();
const route = useRoute();

// The query carries on: a link into signup can name the country, company and step to continue.
const selectRole = (role: Role) => {
  router.push({ path: `/signup/${role}`, query: route.query });
};
</script>

<style scoped lang="scss">
.eyebrow,
.path-eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--phoenix-primary);
  margin-bottom: 0.5rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.subtitle {
  max-width: 640px;
}

.path-card {
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid #e2e4e2;
  background: #ffffff;
  cursor: pointer;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;

  &:hover,
  &:focus-visible {
    border-color: var(--phoenix-primary);
    box-shadow: 0 12px 24px rgba(26, 40, 32, 0.08);
    transform: translateY(-2px);
    outline: none;
  }
}

.path-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eef4ef;
  color: var(--phoenix-primary);
  font-size: 1.15rem;
}

.path-title {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.2;
}

.path-benefit {
  margin-bottom: 0.6rem;
}

.benefit-icon {
  color: var(--phoenix-primary);
  flex: 0 0 auto;
}

.path-button {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-weight: 600;
}

.help-card {
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  border: 1px solid #d9bc78;
  background: #faecca;
}

.help-title {
  font-size: 1.25rem;
  font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
  .path-card {
    transition: none;

    &:hover,
    &:focus-visible {
      transform: none;
    }
  }
}
</style>
